import React from 'react';
import { Link } from 'react-router-dom';

import { PLAY_URL, isRouterPath, normalizeHref } from '../utils/links';

/**
 * A link that knows whether the router owns where it points.
 *
 * Admin-entered links and the game's URL both flow through here, so the same
 * `href` behaves correctly wherever it was typed: a route navigates in place,
 * `/play/` loads the game as its own document, and an absolute URL leaves the
 * site with `rel` set so the page it opens cannot reach back through
 * `window.opener`.
 */
export default function SmartLink({ href: rawHref, children, ...rest }) {
  if (!rawHref) return null;

  const href = normalizeHref(rawHref);

  if (isRouterPath(href)) {
    return (
      <Link to={href} {...rest}>
        {children}
      </Link>
    );
  }

  const external = /^https?:\/\//i.test(href);

  // The game gets a tab of its own.
  //
  // It is a separate application, it is tens of megabytes of engine, and a
  // player who is at a table and follows a link back to the site should not
  // have their session torn down to read an article. Opening it alongside the
  // site rather than over the top of it is also what makes the browser's back
  // button stop being a way to accidentally leave a hand.
  const newTab = external || href === PLAY_URL;

  return (
    <a
      href={href}
      {...(newTab ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
      {...rest}
    >
      {children}
    </a>
  );
}
